import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

import type { Brand, ProductCategory, ProductSpecification } from '@repo/types/product'
import type { ModelId } from '@repo/helpers/ModelIdAndSlug'
import type { FullContactDetails, WebRequestPayload } from '@repo/types/web-requests'
import { fbAddToCartEvent, pushToDataLayer, fbLeadEvent } from '@repo/helpers/util/analytics'
import { getStoredUtmParams, type UtmParams } from '@repo/helpers/util/utm'

import FormApi from '@/api/FormApi.js'
type BasketItemData = {
  modelId: ModelId
  model: string
  brand: Brand
  productCategory: ProductCategory
  productSpecification: ProductSpecification
}
type BasketItems = Record<ModelId, BasketItemData>
type QuoteRequestPayload = Extract<WebRequestPayload, { eventType: 'QUOTE' }>
// interface Utm {
//   utm_source?: string
//   utm_medium?: string
//   utm_campaign?: string
//   utm_term?: string
//   utm_content?: string
//   utm_capture_date?: string
// }

// Keep basket in local store
function getItem(key: string) {
  // Convert JSON to object when fetching
  if (window) {
    const storedItem = window.localStorage.getItem(key)
    if (storedItem) {
      return JSON.parse(storedItem)
    }
  }
  return false
}

function saveItem(key: string, data: unknown) {
  // Convert to JSOn before storing
  if (window) {
    window.localStorage.setItem(key, JSON.stringify(data))
  }
}

// Pass array of keys to delete
function deleteItems(keys: string[]) {
  if (window) {
    keys.forEach((key) => {
      window.localStorage.removeItem(key)
    })
  }
}

export const useBasketStore = defineStore('basket', () => {
  const open = ref<boolean>(false)
  const isSingle = ref<boolean>(true)
  const isInstant = ref<boolean>(false)
  const isLoading = ref<boolean>(false)
  const instant = ref<string>('')
  const basket = ref<BasketItems>({})
  const recaptcha = ref({})

  // const getBasket = computed(() => basket.value)
  const getIsInstant = computed(() => isInstant.value)
  const getIsSingle = computed(() => isSingle.value)
  const getInstantItem = computed(() => instant.value)
  const isBasketOpen = computed(() => open.value)
  const isEmpty = computed(() => Object.keys(basket.value).length === 0)
  const recaptchaData = computed(() => recaptcha.value)
  const basketCount = computed(() => Object.keys(basket.value).length)
  const basketProductIds = computed(() => Object.keys(basket.value) as ModelId[])

  // const quotePayload = computed(() => {
  //   return {
  //     productIds: Object.keys(basket.value),
  //   }
  // })

  function loadBasketFromStorage() {
    const storedBasket = getItem('basket')
    if (storedBasket) {
      basket.value = storedBasket
    }
  }

  function addToBasket(item: BasketItemData) {
    console.log('Adding to basket:', item)
    const { modelId, brand, model, productCategory, productSpecification } = item
    basket.value[item.modelId] = {
      modelId,
      brand,
      model,
      productCategory,
      productSpecification,
    }
    saveItem('basket', basket.value)
    // Fb event
    fbAddToCartEvent(item)
  }

  function addToInstant(item: BasketItemData) {
    instant.value = item.modelId
  }

  function toggleIsInstant() {
    isInstant.value = !isInstant.value
  }

  function combineInstantCompare() {
    // Combine instant and comparison items
    // Not implemented
    // TODO: Delete
  }

  function setInstant(bool: boolean) {
    isInstant.value = bool
  }

  function toggleIsSingle() {
    isSingle.value = !isSingle.value
  }

  function removeFromBasket(modelId: ModelId) {
    const { [modelId]: removedItem, ...rest } = basket.value
    basket.value = rest
    saveItem('basket', basket.value)
  }

  function clearBasket() {
    basket.value = {}
    instant.value = ''
    deleteItems(['basket'])
  }

  function openBasket() {
    open.value = true
  }

  function closeBasket() {
    open.value = false
  }

  function checkoutBasket() {
    // Checkout logic here
    // Unused
    // TODO: Delete
    clearBasket()
  }

  function setRecaptchaData(data: unknown) {
    recaptcha.value = data
  }
  // TODO: Add error handling and loading state - check server validation errors
  /**
   * Quote request submissions - either checks out the basket or a specific product
   * @param contactDetails
   * @param productIds - The IDs of the products to include - replicates instant quote
   */
  async function submitQuote(contactDetails: FullContactDetails, productIds?: ModelId[]) {
    let productIdsToSubmit: ModelId[] = []
    if (productIds) {
      productIdsToSubmit = productIds
    } else {
      productIdsToSubmit = basketProductIds.value
    }
    try {
      const quoteData: QuoteRequestPayload = {
        productIds: productIdsToSubmit,
        contactDetails,
        eventType: 'QUOTE',
        utm: {},
      }
      const utmData = getStoredUtmParams()

      if (utmData) {
        quoteData.utm = utmData
      }
      const submitted = await FormApi.submitQuote(quoteData)
      pushToDataLayer({ event: 'quote_request_submitted' })
      fbLeadEvent()

      // Don't clear the basket if specific product IDs are provided
      // It might be confusing
      if (!productIds) {
        clearBasket()
      }

      return submitted
      // this.isSubmitted = true
    } catch (error) {
      console.error('Error submitting quote:', error)
    }
  }

  return {
    open,
    isSingle,
    isInstant,
    instant,
    basket,
    recaptcha,
    // getters
    // getBasket,
    getInstantItem,
    isBasketOpen,
    recaptchaData,
    basketCount,
    // actions
    loadBasketFromStorage,
    addToBasket,
    addToInstant,
    toggleIsInstant,
    combineInstantCompare,
    setInstant,
    toggleIsSingle,
    removeFromBasket,
    openBasket,
    closeBasket,
    clearBasket,
    checkoutBasket,
    setRecaptchaData,
    submitQuote,
  }
})

// // Keep old code here so that it works until it is fully replaced
// // initial state
// const state = {
//   open: false,
//   isSingle: true,
//   isInstant: false,
//   instant: '',
//   basket: {},
//   recaptcha: {},
//   comparison: [],
// }

// // function deleteItem (key) {
// //   window.localStorage.removeItem(key)
// // }

// // // Clear all localstorage
// // function clearAll(keys) {
// //   if (window) {
// //     window.localStorage.clear()
// //   }
// // }
// // actions
// const actions = {
//   loadBasketFromStorage({ commit }) {
//     const storedBasket = getItem('basket')
//     if (storedBasket) {
//       commit('REPLACE_BASKET', storedBasket)
//     }
//   },
//   addToBasket: ({ commit }, item) => {
//     commit('ADD', item)
//     fbAddToCartEvent(item)
//   },
//   // Handles checkout of single / instant product - so it doesnt interfere with
//   // operation of comparison / basket
//   addToInstant: ({ commit }, item) => {
//     commit('ADD_INSTANT', item)
//   },
//   toggleIsInstant: ({ commit }) => {
//     commit('TOGGLE_INSTANT')
//   },
//   combineInstantCompare: ({ commit }) => {
//     commit('COMBINE_INSTANT_COMPARE')
//   },
//   setInstant: ({ commit }, bool) => {
//     commit('TOGGLE_INSTANT', bool)
//   },
//   toggleIsSingle: ({ commit }) => {
//     commit('TOGGLE_SINGLE')
//   },
//   addAllToBasket: ({ commit }, items) => {
//     items.forEach((item) => {
//       commit('ADD', item)
//     })
//   },
//   removeFromBasket: ({ commit }, modelId) => {
//     commit('REMOVE', modelId)
//   },
//   openBasket: ({ commit }) => {
//     commit('SET_BASKET', true)
//   },
//   closeBasket: ({ commit }) => {
//     commit('SET_BASKET', false)
//   },
//   clearBasket: ({ commit }) => {
//     commit('CLEAR')
//   },
//   checkoutBasket: ({ commit }) => {
//     commit('CLEAR')
//   },
//   setRecaptchaData({ commit }, data) {
//     commit('SET_RECAPTCHA_DATA', data)
//   },
// }

// // mutations
// const mutations = {
//   ADD(state, item) {
//     state.basket[item.modelId] = item
//     // Store basket state in localstore, just overwrite
//     saveItem('basket', state.basket)
//   },
//   ADD_INSTANT(state, item) {
//     state.instant = item.modelId
//   },
//   REPLACE_BASKET(state, data) {
//     state.basket = data
//   },
//   TOGGLE_INSTANT(state, bool) {
//     state.isInstant = bool
//   },
//   TOGGLE_SINGLE(state) {
//     state.isSingle = !state.isSingle
//   },
//   REMOVE(state, modelId) {
//     delete state.basket[modelId]
//     // Store basket state in localstore, just overwrite
//     saveItem('basket', state.basket)
//   },
//   CLEAR(state) {
//     state.basket = {}
//     state.instant = ''
//     deleteItems(['basket'])
//   },
//   TOGGLE_BASKET(state) {
//     state.open = !state.open
//   },
//   SET_BASKET(state, val) {
//     state.open = val
//   },
//   SET_RECAPTCHA_DATA(state, data) {
//     state.recaptcha = data
//   },
// }

// // getters
// const getters = {
//   getBasket: (state) => state.basket,
//   isInstant: (state) => state.instant !== '',
//   isSingle: (state) => state.isSingle,
//   getInstantItem: (state) => state.instant,
//   isBasketOpen: (state) => state.open,
//   recaptchaData: (state) => state.recaptcha,
//   basketCount: (state) => Object.keys(state.basket).length,
// }

// export default { state, mutations, getters, actions }
